import { useEffect, useState, useRef, useCallback } from "react";
import { SortingState, ColumnFiltersState } from "@tanstack/react-table";

/**
 * Comprehensive table state that can be preserved across navigation
 */
export type PreservedTableState = {
  pageIndex: number;
  pageSize: number;
  search: string;
  searchInput: string; // UI search input (before debounce)
  sorting: SortingState;
  columnFilters: ColumnFiltersState;
  filterValue: any[]; // Backend filter payload
  tab?: number; // Optional tab index for multi-tab modules
  version?: string; // Schema version for migration
};

export type UsePreservedTableStateOptions = {
  /**
   * Unique key for this module/table (e.g., "item", "quotation")
   */
  key: string;
  
  /**
   * Optional tab index for multi-tab modules (e.g., 1, 2)
   * If provided, state will be stored per tab
   */
  tabIndex?: number;
  
  /**
   * Default values if no saved state exists
   */
  defaults?: Partial<PreservedTableState>;
  
  /**
   * Whether to clear state on mount (useful for explicit resets)
   */
  clearOnMount?: boolean;
  
  /**
   * Schema version for state migration
   */
  version?: string;
  
  /**
   * Callback when state is restored from storage
   */
  onStateRestored?: (state: PreservedTableState) => void;
  
  /**
   * Callback when state is saved
   */
  onStateSaved?: (state: PreservedTableState) => void;
};

const DEFAULT_VERSION = "1.0.0";

/**
 * Custom hook to preserve table state (filters, pagination, sorting, search, tabs)
 * across navigation. Handles edge cases like corrupted state, storage limits, etc.
 * 
 * @example
 * ```tsx
 * const tableState = usePreservedTableState({
 *   key: "item",
 *   defaults: { pageSize: 10 }
 * });
 * 
 * // Use tableState.pageIndex, tableState.setPageIndex, etc.
 * ```
 */
export default function usePreservedTableState(
  options: UsePreservedTableStateOptions
) {
  const {
    key,
    tabIndex,
    defaults = {},
    clearOnMount = false,
    version = DEFAULT_VERSION,
    onStateRestored,
    onStateSaved,
  } = options;

  // Build storage key with optional tab suffix
  const storageKey = tabIndex !== undefined 
    ? `table-state-${key}-tab${tabIndex}` 
    : `table-state-${key}`;

  // Track if this is the first render
  const isFirstRender = useRef(true);
  const hasRestoredState = useRef(false);
  const isRestoringState = useRef(true); // Track if we're in the restoration phase

  /**
   * Validate and migrate state if needed
   */
  const validateAndMigrateState = useCallback((state: any): PreservedTableState | null => {
    if (!state || typeof state !== "object") {
      return null;
    }

    // Check version compatibility
    if (state.version && state.version !== version) {
      console.warn(`State version mismatch for ${key}. Expected ${version}, got ${state.version}. Resetting to defaults.`);
      return null;
    }

    // Validate required fields
    const validated: PreservedTableState = {
      pageIndex: typeof state.pageIndex === "number" && state.pageIndex >= 0 
        ? state.pageIndex 
        : defaults.pageIndex ?? 0,
      pageSize: typeof state.pageSize === "number" && state.pageSize > 0 
        ? state.pageSize 
        : defaults.pageSize ?? 10,
      search: typeof state.search === "string" ? state.search : defaults.search ?? "",
      searchInput: typeof state.searchInput === "string" ? state.searchInput : defaults.searchInput ?? "",
      sorting: Array.isArray(state.sorting) ? state.sorting : defaults.sorting ?? [],
      columnFilters: Array.isArray(state.columnFilters) ? state.columnFilters : defaults.columnFilters ?? [],
      filterValue: Array.isArray(state.filterValue) ? state.filterValue : defaults.filterValue ?? [],
      tab: tabIndex !== undefined ? tabIndex : state.tab,
      version,
    };

    // Validate pageIndex is not out of bounds (we can't check total pages here, but at least check it's non-negative)
    if (validated.pageIndex < 0) {
      validated.pageIndex = 0;
    }

    return validated;
  }, [version, key, defaults, tabIndex]);

  /**
   * Get default state
   */
  const getDefaultState = useCallback((): PreservedTableState => {
    return {
      pageIndex: defaults.pageIndex ?? 0,
      pageSize: defaults.pageSize ?? 10,
      search: defaults.search ?? "",
      searchInput: defaults.searchInput ?? "",
      sorting: defaults.sorting ?? [],
      columnFilters: defaults.columnFilters ?? [],
      filterValue: defaults.filterValue ?? [],
      tab: tabIndex !== undefined ? tabIndex : defaults.tab,
      version,
    };
  }, [defaults, tabIndex, version]);

  /**
   * Load initial state from sessionStorage
   */
  const loadInitialState = useCallback((): PreservedTableState => {
    if (clearOnMount) {
      try {
        sessionStorage.removeItem(storageKey);
      } catch (error) {
        console.warn(`Failed to clear state for ${storageKey}:`, error);
      }
      return getDefaultState();
    }

    try {
      const stored = sessionStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        const validated = validateAndMigrateState(parsed);
        if (validated) {
          hasRestoredState.current = true;
          if (onStateRestored) {
            onStateRestored(validated);
          }
          return validated;
        }
      }
    } catch (error) {
      console.warn(`Failed to load table state from sessionStorage for ${storageKey}:`, error);
      // Try to clear corrupted data
      try {
        sessionStorage.removeItem(storageKey);
      } catch {
        // Ignore cleanup errors
      }
    }

    return getDefaultState();
  }, [storageKey, clearOnMount, onStateRestored, getDefaultState, validateAndMigrateState]);

  const [state, setState] = useState<PreservedTableState>(loadInitialState);

  // Mark restoration as complete after initial render
  useEffect(() => {
    // Use a small timeout to ensure all initial state updates are done
    const timer = setTimeout(() => {
      isRestoringState.current = false;
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  /**
   * Save state to sessionStorage
   */
  const saveState = useCallback((stateToSave: PreservedTableState) => {
    try {
      const stateToStore = { ...stateToSave, version };
      sessionStorage.setItem(storageKey, JSON.stringify(stateToStore));
      if (onStateSaved) {
        onStateSaved(stateToStore);
      }
    } catch (error) {
      // Handle quota exceeded or other storage errors
      if (error instanceof DOMException && error.name === "QuotaExceededError") {
        console.warn(`SessionStorage quota exceeded for ${storageKey}. Attempting to clear old data.`);
        // Try to clear and retry once
        try {
          // Clear oldest entries (simple approach: clear all table-state entries except current)
          const keysToKeep = [storageKey];
          for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key && key.startsWith("table-state-") && !keysToKeep.includes(key)) {
              sessionStorage.removeItem(key);
            }
          }
          // Retry saving
          sessionStorage.setItem(storageKey, JSON.stringify({ ...stateToSave, version }));
        } catch (retryError) {
          console.error(`Failed to save table state after quota cleanup for ${storageKey}:`, retryError);
        }
      } else {
        console.warn(`Failed to save table state to sessionStorage for ${storageKey}:`, error);
      }
    }
  }, [storageKey, version, onStateSaved]);

  // Save state whenever it changes (debounced would be better, but this ensures consistency)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    saveState(state);
  }, [state, saveState]);

  // Save state on component unmount (cleanup)
  // Use a ref to always get the latest state value
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    return () => {
      // Save the latest state on unmount
      saveState(stateRef.current);
    };
  }, [saveState]);

  // Update setters with proper state management
  const setPageIndex = useCallback((updater: number | ((prev: number) => number)) => {
    setState((s) => ({
      ...s,
      pageIndex: typeof updater === "function" ? updater(s.pageIndex) : updater,
    }));
  }, []);

  const setPageSize = useCallback((updater: number | ((prev: number) => number)) => {
    setState((s) => {
      const newPageSize = typeof updater === "function" ? updater(s.pageSize) : updater;
      return {
        ...s,
        pageSize: newPageSize,
        pageIndex: 0, // Reset to first page when page size changes
      };
    });
  }, []);

  const setSearch = useCallback((updater: string | ((prev: string) => string)) => {
    setState((s) => {
      const newSearch = typeof updater === "function" ? updater(s.search) : updater;
      // Only reset pageIndex if search actually changed and we're not restoring state
      const searchChanged = newSearch !== s.search;
      return {
        ...s,
        search: newSearch,
        pageIndex: searchChanged && !isRestoringState.current ? 0 : s.pageIndex,
      };
    });
  }, []);

  const setSearchInput = useCallback((updater: string | ((prev: string) => string)) => {
    setState((s) => ({
      ...s,
      searchInput: typeof updater === "function" ? updater(s.searchInput) : updater,
    }));
  }, []);

  const setSorting = useCallback((
    updater: SortingState | ((prev: SortingState) => SortingState)
  ) => {
    setState((s) => {
      const newSorting = typeof updater === "function" ? updater(s.sorting) : updater;
      // Only reset pageIndex if sorting actually changed and we're not restoring state
      const sortingChanged = JSON.stringify(newSorting) !== JSON.stringify(s.sorting);
      return {
        ...s,
        sorting: newSorting,
        pageIndex: sortingChanged && !isRestoringState.current ? 0 : s.pageIndex,
      };
    });
  }, []);

  const setColumnFilters = useCallback((
    updater: ColumnFiltersState | ((prev: ColumnFiltersState) => ColumnFiltersState)
  ) => {
    setState((s) => {
      const newFilters = typeof updater === "function" ? updater(s.columnFilters) : updater;
      // Only reset pageIndex if filters actually changed and we're not restoring state
      const filtersChanged = JSON.stringify(newFilters) !== JSON.stringify(s.columnFilters);
      return {
        ...s,
        columnFilters: newFilters,
        pageIndex: filtersChanged && !isRestoringState.current ? 0 : s.pageIndex,
      };
    });
  }, []);

  const setFilterValue = useCallback((
    updater: any[] | ((prev: any[]) => any[])
  ) => {
    setState((s) => {
      const newFilterValue = typeof updater === "function" ? updater(s.filterValue) : updater;
      // Only reset pageIndex if filter value actually changed and we're not restoring state
      const filterValueChanged = JSON.stringify(newFilterValue) !== JSON.stringify(s.filterValue);
      return {
        ...s,
        filterValue: newFilterValue,
        pageIndex: filterValueChanged && !isRestoringState.current ? 0 : s.pageIndex,
      };
    });
  }, []);

  const setTab = useCallback((updater: number | ((prev: number) => number)) => {
    setState((s) => ({
      ...s,
      tab: typeof updater === "function" ? updater(s.tab ?? tabIndex ?? 1) : updater,
    }));
  }, [tabIndex]);

  /**
   * Reset state to defaults
   */
  const resetState = useCallback(() => {
    const defaultState = getDefaultState();
    setState(defaultState);
    try {
      sessionStorage.removeItem(storageKey);
    } catch (error) {
      console.warn(`Failed to clear state for ${storageKey}:`, error);
    }
  }, [getDefaultState, storageKey]);

  /**
   * Clear state without resetting to defaults (useful for cleanup)
   */
  const clearState = useCallback(() => {
    try {
      sessionStorage.removeItem(storageKey);
    } catch (error) {
      console.warn(`Failed to clear state for ${storageKey}:`, error);
    }
  }, [storageKey]);

  return {
    // State values
    ...state,
    
    // Setters
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    setTab,
    
    // Utility functions
    resetState,
    clearState,
    
    // Metadata
    hasRestoredState: hasRestoredState.current,
    storageKey,
  };
}

